import { formatDeckKey, STANDARD_DECK_KEY } from '@/config/objects/deck-shares';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';

interface DeckKeyDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

/**
 * Picks which deck a share link opens. Options come from the live content
 * documents (`GET /deck-shares/decks`) rather than a hardcoded list, so a share
 * can never be minted against a deck variant that no longer exists — the link
 * would resolve to nothing in the prospect's browser.
 *
 * While the list is loading (or if it fails) the standard deck is still
 * offered: it is the answer nine times out of ten, and a picker that renders
 * empty would block the form.
 */
export function DeckKeyDropdown({ value, onChange, className }: DeckKeyDropdownProps) {
  const apiClient = useApiClient();

  const { data } = useQuery<{ success: boolean; data: string[] }>({
    queryKey: ['DeckShareDeckKeys'],
    queryFn: () => apiClient.get('/deck-shares/decks'),
  });

  const keys = data?.data?.length ? data.data : [STANDARD_DECK_KEY];

  return (
    <Select value={value ?? STANDARD_DECK_KEY} onValueChange={onChange}>
      <SelectTrigger className={className ?? 'w-full'}>
        <SelectValue placeholder="Select deck" />
      </SelectTrigger>
      <SelectContent>
        {keys.map((key) => (
          <SelectItem key={key} value={key}>
            {formatDeckKey(key)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
